import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Check, X, Clock, CheckCircle2, XCircle, Activity, ChevronRight, RefreshCw, Target } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { courseRequestsApi, enrollmentsApi } from '../../lib/api';
import { toast } from 'sonner';

export const RequestsManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const data = await courseRequestsApi.getAll();
      setRequests(data);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      await courseRequestsApi.update(requestId, { status });
      toast.success(`Request ${status}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeEnrollmentStatus = async (requestId, newStatus) => {
    setUpdatingStatus(requestId);
    try {
      await enrollmentsApi.updateStatus(requestId, newStatus);
      toast.success(`Enrollment status updated to ${newStatus.replace('_', ' ')}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500',
    approved: 'bg-green-500',
    rejected: 'bg-red-500',
    in_progress: 'bg-blue-500',
    exam_pending: 'bg-amber-500',
    completed: 'bg-purple-500'
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Course Requests</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filter:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="exam_pending">Exam Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {user?.role === 'org_admin' && (
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-sm text-blue-600 dark:text-blue-400">
            <strong>Org Admin:</strong> You can approve or reject course requests from students in your school.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-lg" />)}
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="empty-state py-12 rounded-xl bg-card border border-border">
          <Target className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No requests</h3>
          <p className="text-muted-foreground">
            {filter === 'all' ? 'Course requests will appear here.' : `No ${filter} requests found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div key={request.request_id} className="p-4 rounded-lg bg-card border border-border flex items-center justify-between" data-testid={`request-${request.request_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{request.user?.name || request.user_id}</span>
                  <span className={`px-2 py-0.5 rounded text-xs text-white ${statusColors[request.status]}`}>
                    {request.status.replace('_', ' ')}
                  </span>
                  {request.user?.role && (
                    <Badge variant="outline" className="text-xs">{request.user.role.replace('_', ' ')}</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {request.course?.title || 'Unknown Course'} • {request.course?.language?.toUpperCase() || request.course?.category?.replace('_', ' ').toUpperCase() || 'COURSE'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Requested: {new Date(request.requested_at).toLocaleDateString()}
                  {request.approved_at && ` • Approved: ${new Date(request.approved_at).toLocaleDateString()}`}
                  {request.progress !== undefined && request.progress > 0 && ` • Progress: ${Math.round(request.progress)}%`}
                </p>
              </div>
              <div className="flex gap-2">
                {request.status === 'pending' ? (
                  <>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateStatus(request.request_id, 'approved')} data-testid={`approve-${request.request_id}`}>
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleUpdateStatus(request.request_id, 'rejected')} data-testid={`reject-${request.request_id}`}>
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </>
                ) : request.status !== 'rejected' && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="outline"
                        disabled={updatingStatus === request.request_id}
                        data-testid={`status-dropdown-${request.request_id}`}
                      >
                        {updatingStatus === request.request_id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            Change Status
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {request.status !== 'approved' && (
                        <DropdownMenuItem onClick={() => handleChangeEnrollmentStatus(request.request_id, 'approved')}>
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          Set to Approved
                        </DropdownMenuItem>
                      )}
                      {request.status !== 'in_progress' && (
                        <DropdownMenuItem onClick={() => handleChangeEnrollmentStatus(request.request_id, 'in_progress')}>
                          <Activity className="w-4 h-4 mr-2 text-blue-500" />
                          Set to In Progress
                        </DropdownMenuItem>
                      )}
                      {request.status !== 'completed' && (
                        <DropdownMenuItem onClick={() => handleChangeEnrollmentStatus(request.request_id, 'completed')}>
                          <CheckCircle2 className="w-4 h-4 mr-2 text-purple-500" />
                          Mark as Completed
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleChangeEnrollmentStatus(request.request_id, 'rejected')}
                        className="text-destructive"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject Enrollment
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Growth Analytics Component
